import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { getCart } from "@/lib/cart";
import { useAITransition } from "@/components/AITransition";
import { toast } from "sonner";
import { Sparkles, LogOut, LayoutDashboard, Users, CalendarDays, ShoppingBag, ClipboardList, Shield, Bell, User, Home, MapPin, ChevronDown, Search, Store } from "lucide-react";
import api from "@/lib/api";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user?.role === "admin";
  const [pendingCount, setPendingCount] = useState(0);
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    // Count upcoming occasions within next 30 days as "notifications"
    api.get("/relationships/upcoming?limit=20")
      .then((r) => setPendingCount(r.data.filter((e) => e.days_left <= 30).length))
      .catch(() => setPendingCount(0));
    setCartCount(getCart().reduce((s, i) => s + (i.quantity || 1), 0));
  }, [location.pathname]);

  // Live cart badge: cart.js dispatches "cart-change" on every mutation.
  useEffect(() => {
    const refresh = () => setCartCount(getCart().reduce((s, i) => s + (i.quantity || 1), 0));
    window.addEventListener("cart-change", refresh);
    return () => window.removeEventListener("cart-change", refresh);
  }, []);

  const handleLogout = async () => { await logout(); navigate("/"); };
  const playAI = useAITransition();

  const topNav = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/relationships", label: "People", icon: Users },
    { to: "/calendar", label: "Calendar", icon: CalendarDays },
    { to: "/shop", label: "Shop", icon: ShoppingBag },
    { to: "/bookings", label: "Orders", icon: ClipboardList },
  ];

  return (
    <div className="min-h-screen bg-linen">
      <header className="sticky top-0 z-40 bg-white border-b border-warm" data-testid="top-navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          {/* Left: logo */}
          <Link to="/dashboard" className="flex items-center gap-2 flex-shrink-0" data-testid="brand-link">
            <div className="w-10 h-10 rounded-2xl rose-glow flex items-center justify-center shadow-lg shadow-rose-500/30">
              <Sparkles className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
            <div className="hidden sm:block">
              <span className="font-display text-xl block leading-none">CelebrAI</span>
              <span className="text-[10px] text-charcoal/50 leading-tight">Make Every Relationship Special</span>
            </div>
          </Link>

          {/* Center: desktop nav links */}
          <nav className="hidden md:flex items-center gap-1">
            {topNav.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${isActive ? "bg-charcoal text-white shadow-md" : "text-charcoal/70 hover:bg-linen-2 hover:text-charcoal"}`
                }>
                <n.icon className="w-4 h-4" strokeWidth={1.75} />
                {n.label}
              </NavLink>
            ))}
            {isAdmin && (
              <NavLink to="/admin" data-testid="nav-admin"
                className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${isActive ? "rose-glow text-white shadow-lg shadow-rose-500/30" : "text-terracotta hover:bg-linen-2"}`
                }>
                <Shield className="w-4 h-4" strokeWidth={1.75} />
                Admin
              </NavLink>
            )}
          </nav>

          {/* Location pill (mobile center / desktop before icons) */}
          <button
            onClick={() => toast.info("We're currently delivering in Jaipur — more cities soon!")}
            data-testid="location-pill"
            className="md:ml-0 inline-flex items-center gap-1.5 rounded-full bg-linen-2 border border-warm px-3.5 py-2 hover:bg-white transition-colors"
          >
            <MapPin className="w-4 h-4 text-rose-500" strokeWidth={2.25} />
            <span className="text-sm font-semibold text-charcoal">Jaipur</span>
            <ChevronDown className="w-3.5 h-3.5 text-charcoal/50" strokeWidth={2.25} />
          </button>

          {/* Right: search, cart, bell, profile, logout */}
          <div className="flex items-center gap-1.5 sm:gap-2">
            <button
              onClick={() => navigate("/shop")}
              className="rounded-full p-2.5 hover:bg-linen-2 transition"
              data-testid="nav-search"
              aria-label="Search">
              <Search className="w-5 h-5 text-charcoal" strokeWidth={1.75} />
            </button>
            <button
              onClick={() => navigate("/checkout")}
              className="relative rounded-full p-2.5 hover:bg-linen-2 transition"
              data-testid="nav-cart"
              aria-label="Cart">
              <ShoppingBag className="w-5 h-5 text-charcoal" strokeWidth={1.75} />
              {cartCount > 0 && (
                <span data-testid="nav-cart-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>
            <button
              onClick={() => navigate("/dashboard")}
              className="relative rounded-full p-2.5 hover:bg-linen-2 transition hidden sm:flex"
              data-testid="notif-bell"
              aria-label="Notifications">
              <Bell className="w-5 h-5 text-charcoal" strokeWidth={1.75} />
              {pendingCount > 0 && (
                <span data-testid="notif-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">
                  {pendingCount}
                </span>
              )}
            </button>
            <Link to="/profile" data-testid="avatar-link" className="rounded-full overflow-hidden ring-2 ring-linen-2 shadow-sm">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-9 h-9 object-cover" />
              ) : (
                <div className="w-9 h-9 bg-gradient-to-br from-rose-400 to-purple-500 flex items-center justify-center text-sm font-bold text-white">
                  {(user?.name || "?").slice(0, 1).toUpperCase()}
                </div>
              )}
            </Link>
            <button onClick={handleLogout} data-testid="logout-btn"
              className="hidden sm:flex rounded-full p-2.5 hover:bg-linen-2" title="Logout">
              <LogOut className="w-5 h-5 text-charcoal" strokeWidth={1.75} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-5 sm:py-8 pb-28 md:pb-10">{children}</main>

      {/* Mobile bottom nav: Home / Shop / Plan with AI / Experiences / Orders */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-white/95 backdrop-blur-xl border-t border-warm pb-[env(safe-area-inset-bottom)]" data-testid="bottom-nav">
        <div className="flex items-end justify-around px-2 pt-2 pb-3 relative">
          <BottomTab to="/dashboard" icon={Home} label="Home" testId="btm-home" />
          <BottomTab to="/shop" icon={Store} label="Shop" testId="btm-shop" />

          {/* Centered elevated Plan with AI — plays the shared AI transition first */}
          <button
            onClick={(e) => playAI(() => navigate("/planner"), e)}
            data-testid="btm-ai-plan"
            className="relative -mt-8 flex flex-col items-center">
            <div className="w-14 h-14 rounded-full flex items-center justify-center shadow-xl shadow-rose-500/40 border-4 border-white"
              style={{ background: "linear-gradient(135deg, #E11D48 0%, #A855F7 100%)" }}>
              <Sparkles className="w-6 h-6 text-white" strokeWidth={2.25} />
            </div>
            <span className="text-[10px] font-semibold text-rose-600 mt-1">Plan with AI</span>
          </button>

          <button
            onClick={() => navigate("/dashboard#experiences")}
            data-testid="btm-experiences"
            className="flex flex-col items-center gap-1 flex-1 py-1 text-charcoal/60 transition-colors">
            <Sparkles className="w-5 h-5" strokeWidth={1.75} />
            <span className="text-[10px] font-semibold">Experiences</span>
          </button>
          <BottomTab to="/bookings" icon={ClipboardList} label="Orders" testId="btm-orders" />
        </div>
      </nav>
    </div>
  );
}

function BottomTab({ to, icon: Icon, label, testId }) {
  return (
    <NavLink to={to} data-testid={testId}
      className={({ isActive }) =>
        `flex flex-col items-center gap-1 flex-1 py-1 transition-colors ${isActive ? "text-rose-500" : "text-charcoal/60"}`
      }>
      {({ isActive }) => (
        <>
          <Icon className="w-5 h-5" strokeWidth={isActive ? 2.25 : 1.75} />
          <span className="text-[10px] font-semibold">{label}</span>
        </>
      )}
    </NavLink>
  );
}
